import {memo, useState} from 'react';

import {Form} from 'react-final-form';
import {useSelector} from 'react-redux';

import {selectUserError, selectUserErrorRequireCaptcha} from '~/shared/store/selectors';
import useFormAutoFocus from '~/shared/hooks/useFormAutoFocus';
import FormField from '~/shared/components/FormField';
import {
  FieldWrapper,
  FormWrapper,
  LoginFormFieldsWrapper,
  Email,
  StyledErrorText,
  LinkButtonsWrapper,
  BlackLinkButton,
} from '~/shared/components/loginStyled';
import FormPieInput from '~/shared/components/FormField/FormInput/FormPieInput';
import PieButton from '~/shared/theme/pie/PieButton';
import {getLocalizationService} from '~/shared/services/localisationService';

import ResentPasswordView from './ResentPasswordView';

type PasswordFormProps = {
  onBackClick?: () => void;
  onSubmitLoginWithEmailAndPassword: (values: {password: string; reCaptchaResponse?: string}) => Promise<void>;
  email: string;
  onForgotPassword: () => void;
};

const PasswordForm = ({onBackClick, onSubmitLoginWithEmailAndPassword, email, onForgotPassword}: PasswordFormProps) => {
  const {t} = getLocalizationService();
  const [hasResentThePassword, setHasResetThePassword] = useState(false);
  const [resErrorDisplayAllowed, setResErrorDisplayAllowed] = useState(false);
  const userError = useSelector(selectUserError);
  const isRequireCaptcha = useSelector(selectUserErrorRequireCaptcha);
  const formWrapRef = useFormAutoFocus();

  const onForgotPasswordHandler = () => {
    setHasResetThePassword(true);
    onForgotPassword();
  };

  const submitHandler = async (values: {password: string; reCaptchaResponse?: string}) => {
    try {
      await onSubmitLoginWithEmailAndPassword(values);
    } catch (err) {
      return {err};
    } finally {
      setResErrorDisplayAllowed(true);
    }
  };

  const onFocusHandler = () => {
    setResErrorDisplayAllowed(false);
  };

  if (hasResentThePassword) {
    return (
      <ResentPasswordView onBackClick={onBackClick} />
    );
  }

  return (
    <>
      <Form
        onSubmit={submitHandler}
        validateOnBlur
        render={({handleSubmit}) => (
          <FormWrapper onSubmit={handleSubmit} ref={formWrapRef}>
            <Email id="modal-title">{email}</Email>
            <LoginFormFieldsWrapper>
              <FieldWrapper>
                {/*@ts-expect-error - FormField should be refactored to TypeScript*/}
                <FormField
                  {...{
                    name: 'password',
                    placeholder: t('password'),
                    type: 'currentPassword',
                    render: FormPieInput,
                    required: true,
                    selectOnFocus: true,
                    className: 'white-border-on-contrast',
                    shouldShowError: true,
                    hasForceError: resErrorDisplayAllowed && !!userError,
                    isPieStyle: true,
                    onFocus: onFocusHandler,
                  }}
                />
              </FieldWrapper>
              {/*@ts-expect-error - FormField should be refactored to TypeScript*/}
              {isRequireCaptcha && <FormField name="reCaptchaResponse" type="recaptcha" />}
              {resErrorDisplayAllowed && !!userError && <StyledErrorText>{t('entry_email_or_password_are_incorrect')}</StyledErrorText>}
            </LoginFormFieldsWrapper>
            <PieButton type="submit" variant="primaryLarge">{t('lets_go')}</PieButton>
          </FormWrapper>
        )}
      />
      <LinkButtonsWrapper>
        <BlackLinkButton onClick={onForgotPasswordHandler}>{t('forgot_password')}</BlackLinkButton>
      </LinkButtonsWrapper>
    </>
  );
};

export default memo(PasswordForm);
